import { CropSelectionKind } from 'api-validations/tailor'
import { useMemo } from 'react'
import { useLocalization } from 'shared-ui/localization'
import { useOperationSeasons } from 'shared-ui/operation-wide-data'

import type { Season } from './reportingTypes'

/**
 * The season selector's options: every crop the operation has data for, plus an all-crops entry for
 * each year that grew more than one. A year with a single crop already has that report, so a second
 * entry covering the same ground would only ask the reader which of two identical reports they
 * meant.
 *
 * Scoped to reporting rather than added to the operation-wide season list, which answers "what did
 * this operation grow" for callers that have no notion of covering several crops at once.
 */
export const useReportingSeasons = (): Season[] => {
  const operationSeasons = useOperationSeasons()
  const { t } = useLocalization()

  return useMemo(() => {
    const cropCountByYear = operationSeasons.reduce(
      (counts, season) => counts.set(season.year, (counts.get(season.year) ?? 0) + 1),
      new Map<number, number>()
    )

    const allCropsLabel = t(($) => $.reporting.allCrops)

    return operationSeasons.flatMap((season, index) => {
      const cropSeason: Season = {
        year: season.year,
        crop: { kind: CropSelectionKind.Single, crop: season.cropKey },
        cropLabel: season.cropLabel
      }

      // Operation seasons arrive sorted by year, so the last crop of a year is where its all-crops
      // entry belongs — under the crops it covers rather than above them.
      const isLastCropOfYear = operationSeasons[index + 1]?.year !== season.year
      const yearGrewSeveralCrops = (cropCountByYear.get(season.year) ?? 0) > 1

      if (!isLastCropOfYear || !yearGrewSeveralCrops) return [cropSeason]

      return [
        cropSeason,
        { year: season.year, crop: { kind: CropSelectionKind.All }, cropLabel: allCropsLabel }
      ]
    })
  }, [operationSeasons, t])
}
